import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

const RBACContext = createContext(null);

// Default RBAC settings (fallback if API fails)
const defaultRBACSettings = {
  permissions: {
    dashboard_view: { super_admin: true, admin: true, site_admin: true, department_admin: true, end_user: true },
    bulletin_post: { super_admin: true, admin: true, site_admin: true, department_admin: true, end_user: false },
    bulletin_view: { super_admin: true, admin: true, site_admin: true, department_admin: true, end_user: true },
    ticket_view: { super_admin: "all", admin: "all", site_admin: "dealership", department_admin: "conditional", end_user: "own" },
    ticket_create: { super_admin: true, admin: true, site_admin: true, department_admin: true, end_user: true },
    ticket_update: { super_admin: true, admin: true, site_admin: true, department_admin: true, end_user: false },
    ticket_comment: { super_admin: true, admin: true, site_admin: true, department_admin: true, end_user: false },
    ticket_private_comment: { super_admin: true, admin: true, site_admin: false, department_admin: false, end_user: false },
    ticket_assign: { super_admin: true, admin: true, site_admin: true, department_admin: true, end_user: false },
    credentials_send: { super_admin: true, admin: true, site_admin: true, department_admin: true, end_user: false },
    hr_view: { super_admin: true, admin: true, site_admin: "dealership", department_admin: "conditional", end_user: false },
    hr_create: { super_admin: true, admin: true, site_admin: true, department_admin: "conditional", end_user: false },
    hr_update: { super_admin: true, admin: true, site_admin: true, department_admin: "conditional", end_user: false },
    history_view: { super_admin: "all", admin: "all", site_admin: "dealership", department_admin: "conditional", end_user: "own" },
    analytics_view: { super_admin: true, admin: true, site_admin: true, department_admin: false, end_user: false },
    settings_users: { super_admin: true, admin: true, site_admin: false, department_admin: false, end_user: false },
    settings_connections: { super_admin: true, admin: true, site_admin: false, department_admin: false, end_user: false },
    settings_site: { super_admin: true, admin: true, site_admin: false, department_admin: false, end_user: false },
    view_testing: { super_admin: true, admin: true, site_admin: false, department_admin: false, end_user: false }
  },
  privileged_departments: ["HR", "IT", "Administration"]
};

export function RBACProvider({ children, user }) {
  const [rbacSettings, setRbacSettings] = useState(defaultRBACSettings);
  const [loading, setLoading] = useState(true);

  const fetchRBACSettings = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      // Only super_admin and admin can fetch RBAC settings
      if (user.role === 'super_admin' || user.role === 'admin') {
        const response = await axios.get(`${API}/admin/rbac`);
        setRbacSettings(response.data);
      }
    } catch (error) {
      console.log('Using default RBAC settings');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchRBACSettings();
  }, [fetchRBACSettings]);

  // Check if user has permission for a specific action
  const hasPermission = useCallback((permissionKey, effectiveRole, effectiveDepartment) => {
    const role = effectiveRole || user?.role;
    const department = effectiveDepartment || user?.department;
    
    if (!role || !rbacSettings?.permissions?.[permissionKey]) {
      return false;
    }

    const permission = rbacSettings.permissions[permissionKey][role];
    
    // Boolean permissions
    if (permission === true) return true;
    if (permission === false) return false;
    
    // Scoped permissions
    if (permission === "all") return true;
    if (permission === "dealership") return true; // Frontend allows, backend filters
    if (permission === "own") return true; // Frontend allows, backend filters
    if (permission === "conditional") {
      // Check if user is in privileged department
      const privilegedDepts = rbacSettings.privileged_departments || [];
      return privilegedDepts.includes(department);
    }
    
    return false;
  }, [user, rbacSettings]);

  // Get the scope of permission (for filtering logic)
  const getPermissionScope = useCallback((permissionKey, effectiveRole) => {
    const role = effectiveRole || user?.role;
    
    if (!role || !rbacSettings?.permissions?.[permissionKey]) {
      return null;
    }

    return rbacSettings.permissions[permissionKey][role];
  }, [user, rbacSettings]);

  // Check if a department is privileged
  const isPrivilegedDepartment = useCallback((department) => {
    const dept = department || user?.department;
    return rbacSettings.privileged_departments?.includes(dept) || false;
  }, [user, rbacSettings]);

  const value = {
    rbacSettings,
    loading,
    hasPermission,
    getPermissionScope,
    isPrivilegedDepartment,
    refreshRBAC: fetchRBACSettings
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
}

export function useRBAC() {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
}

export default RBACContext;
